<template>
  <div class="feedback-manage-view">
    <management-table
      title="反馈管理"
      eyebrow="Feedback Center"
      description="查看用户提交的问题与建议，记录处理进度并向用户回复。"
      :data="tableData"
      :loading="loading"
      :current-page="query.pageNum"
      :page-size="query.pageSize"
      :page-sizes="[10, 20, 50]"
      :total="total"
      pagination-layout="total, sizes, prev, pager, next, jumper"
      empty-text="暂无符合条件的反馈"
      @size-change="handleSizeChange"
      @current-change="handlePageChange">
      <template slot="filters">
        <div class="search-item feedback-keyword-filter">
          <label class="search-label">反馈内容:</label>
          <el-input
            v-model.trim="query.keyword"
            class="search-input"
            clearable
            placeholder="搜索标题或内容"
            @keyup.enter.native="search" />
        </div>
        <div class="search-item feedback-select-filter">
          <label class="search-label">反馈类型:</label>
          <el-select v-model="query.feedbackType" class="search-select" clearable placeholder="全部类型">
            <el-option
              v-for="item in feedbackTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value" />
          </el-select>
        </div>
        <div class="search-item feedback-select-filter">
          <label class="search-label">处理状态:</label>
          <el-select v-model="query.status" class="search-select" clearable placeholder="全部状态">
            <el-option
              v-for="item in statuses"
              :key="item.value"
              :label="item.label"
              :value="item.value" />
          </el-select>
        </div>
      </template>
      <template slot="actions">
        <el-button type="primary" icon="el-icon-search" class="search-btn" :loading="loading" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh-left" class="feedback-reset-btn" @click="resetFilters">重置</el-button>
      </template>
      <template slot="columns">
          <el-table-column label="提交用户" min-width="150" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="user-cell">
                <strong>{{ scope.row.userName || '未知用户' }}</strong>
                <small>{{ scope.row.account || ('ID ' + scope.row.userId) }}</small>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="类型" width="110">
            <template slot-scope="scope">{{ typeLabel(scope.row.feedbackType) }}</template>
          </el-table-column>
          <el-table-column prop="title" label="反馈标题" min-width="190" show-overflow-tooltip />
          <el-table-column prop="content" label="反馈内容" min-width="260" show-overflow-tooltip />
          <el-table-column prop="latestReply" label="最近回复" min-width="220" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.latestReply || '—' }}</template>
          </el-table-column>
          <el-table-column prop="createTime" label="提交时间" width="168" />
          <el-table-column label="状态" width="94">
            <template slot-scope="scope">
              <span class="status-pill" :class="statusClass(scope.row.status)">
                {{ statusLabel(scope.row.status) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button
                class="feedback-handle-btn"
                :icon="scope.row.status === 2 ? 'el-icon-view' : 'el-icon-chat-dot-round'"
                @click="openDetail(scope.row.id)">
                {{ scope.row.status === 2 ? '查看' : '处理' }}
              </el-button>
            </template>
          </el-table-column>
      </template>
    </management-table>

    <el-dialog
      :visible.sync="detailVisible"
      width="820px"
      top="5vh"
      custom-class="feedback-detail-dialog"
      :close-on-click-modal="false">
      <div slot="title" class="dialog-title">
        <div>
          <strong>反馈处理</strong>
        </div>
        <span v-if="detail.id" class="status-pill" :class="statusClass(detail.status)">
          {{ statusLabel(detail.status) }}
        </span>
      </div>

      <div v-loading="detailLoading" class="dialog-content">
        <template v-if="detail.id">
          <div class="feedback-summary">
            <div class="summary-meta">
              <span><i class="el-icon-user"></i>{{ detail.userName || '未知用户' }}（{{ detail.account || detail.userId }}）</span>
              <span><i class="el-icon-collection-tag"></i>{{ typeLabel(detail.feedbackType) }}</span>
              <span><i class="el-icon-time"></i>{{ detail.createTime }}</span>
            </div>
            <h2>{{ detail.title }}</h2>
            <p>{{ detail.content }}</p>
          </div>

          <div class="record-section">
            <div class="section-title">
              <strong>处理记录</strong>
              <span>{{ detail.records.length }} 条</span>
            </div>
            <el-timeline v-if="detail.records.length">
              <el-timeline-item
                v-for="record in detail.records"
                :key="record.id"
                :timestamp="record.createTime"
                placement="top"
                :color="record.status === 2 ? '#39d98a' : '#36bffa'">
                <div class="record-card">
                  <div>
                    <strong>{{ record.handlerName || ('管理员 ' + record.handlerId) }}</strong>
                    <span>{{ statusLabel(record.status) }}</span>
                  </div>
                  <p v-if="record.replyContent">{{ record.replyContent }}</p>
                  <p v-else class="muted">仅更新了处理状态</p>
                </div>
              </el-timeline-item>
            </el-timeline>
            <div v-else class="empty-record">尚无处理记录</div>
          </div>

          <el-form v-if="nextStatus(detail.status) !== null" label-position="top" class="handle-form">
            <div class="section-title"><strong>本次处理</strong></div>
            <div class="status-transition">
              <span class="status-pill" :class="statusClass(detail.status)">{{ statusLabel(detail.status) }}</span>
              <i class="el-icon-right"></i>
              <span class="status-pill" :class="statusClass(nextStatus(detail.status))">
                {{ statusLabel(nextStatus(detail.status)) }}
              </span>
            </div>
            <el-form-item label="回复用户（可选）">
              <el-input
                v-model="handleForm.replyContent"
                type="textarea"
                :rows="4"
                maxlength="2000"
                show-word-limit
                placeholder="填写处理结论、解决办法或需要用户补充的信息" />
            </el-form-item>
          </el-form>
          <div v-else class="completed-hint">
            <i class="el-icon-circle-check"></i>
            该反馈已处理完成，无需继续变更状态。
          </div>
        </template>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">{{ nextStatus(detail.status) === null ? '关闭' : '取消' }}</el-button>
        <el-button
          v-if="nextStatus(detail.status) !== null"
          type="primary"
          :loading="submitting"
          :disabled="detailLoading || !detail.id"
          @click="submitHandle">
          {{ nextActionLabel(detail.status) }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ManagementTable from '../../components/ManagementTable.vue';

export default {
  name: 'FeedbackManage',
  components: {
    ManagementTable
  },
  data() {
    return {
      loading: false,
      detailLoading: false,
      submitting: false,
      detailVisible: false,
      total: 0,
      tableData: [],
      detail: { records: [] },
      query: {
        pageNum: 1,
        pageSize: 20,
        keyword: '',
        feedbackType: null,
        status: null
      },
      handleForm: {
        replyContent: ''
      },
      feedbackTypes: [
        { value: 1, label: '功能建议' },
        { value: 2, label: '使用问题' },
        { value: 3, label: '故障 / Bug' },
        { value: 9, label: '其他反馈' }
      ],
      statuses: [
        { value: 0, label: '待处理' },
        { value: 1, label: '处理中' },
        { value: 2, label: '已处理' }
      ]
    };
  },
  mounted() {
    if (Number(localStorage.getItem('a-xny-level')) !== 1) {
      this.$message.error('仅平台管理员可以访问反馈管理');
      this.$router.replace({ name: 'indexVue' });
      return;
    }
    this.loadPage();
  },
  methods: {
    loadPage() {
      this.loading = true;
      this.$request.post('/api/admin/feedback/page', this.query).then(response => {
        if (!response.data.result) {
          throw new Error(response.data.message || '反馈列表加载失败');
        }
        const page = response.data.data || {};
        this.tableData = page.list || [];
        this.total = page.totalCount || 0;
      }).catch(error => {
        this.$message.error(this.errorMessage(error, '反馈列表加载失败'));
      }).finally(() => {
        this.loading = false;
      });
    },
    search() {
      this.query.pageNum = 1;
      this.loadPage();
    },
    resetFilters() {
      this.query.keyword = '';
      this.query.feedbackType = null;
      this.query.status = null;
      this.search();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.search();
    },
    handlePageChange(page) {
      this.query.pageNum = page;
      this.loadPage();
    },
    openDetail(id) {
      this.detailVisible = true;
      this.detailLoading = true;
      this.detail = { records: [] };
      this.$request.get('/api/admin/feedback/' + id).then(response => {
        if (!response.data.result) {
          throw new Error(response.data.message || '反馈详情加载失败');
        }
        this.detail = response.data.data || { records: [] };
        this.detail.records = this.detail.records || [];
        this.handleForm = {
          replyContent: ''
        };
      }).catch(error => {
        this.$message.error(this.errorMessage(error, '反馈详情加载失败'));
        this.detailVisible = false;
      }).finally(() => {
        this.detailLoading = false;
      });
    },
    submitHandle() {
      const targetStatus = this.nextStatus(this.detail.status);
      if (targetStatus === null) {
        this.$message.warning('该反馈已处理完成');
        return;
      }
      const reply = (this.handleForm.replyContent || '').trim();
      this.submitting = true;
      this.$request.post('/api/admin/feedback/' + this.detail.id + '/handle', {
        status: targetStatus,
        replyContent: reply
      }).then(response => {
        if (!response.data.result) {
          throw new Error(response.data.message || '反馈处理失败');
        }
        this.$message.success(reply ? '处理进度已更新并回复用户' : '反馈状态已更新');
        this.detailVisible = false;
        this.loadPage();
      }).catch(error => {
        this.$message.error(this.errorMessage(error, '反馈处理失败'));
      }).finally(() => {
        this.submitting = false;
      });
    },
    typeLabel(value) {
      const item = this.feedbackTypes.find(type => type.value === value);
      return item ? item.label : '未知类型';
    },
    statusLabel(value) {
      const item = this.statuses.find(status => status.value === value);
      return item ? item.label : '未知状态';
    },
    statusClass(value) {
      return value === 2 ? 'is-done' : (value === 1 ? 'is-processing' : 'is-pending');
    },
    nextStatus(value) {
      if (value === 0) {
        return 1;
      }
      if (value === 1) {
        return 2;
      }
      return null;
    },
    nextActionLabel(value) {
      return value === 0 ? '开始处理' : '完成处理';
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : ((error && error.message) || fallback);
    }
  }
};
</script>

<style scoped>
.feedback-manage-view {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  color: var(--solar-text-strong, #dff7ff);
}

.feedback-keyword-filter { flex-basis: 330px; }
.feedback-select-filter { flex-basis: 220px; }
.feedback-reset-btn { height: 36px; margin-left: 0 !important; border-color: rgba(var(--solar-border-strong-rgb, 63, 184, 255), 0.58); background: var(--solar-surface, #002b4d); color: var(--solar-text, #9edcf4); }
.feedback-reset-btn:hover { border-color: var(--solar-border-strong, #00bff3); background: var(--solar-raised, #003f68); color: var(--solar-text-strong, #dff8ff); }
.feedback-handle-btn {
  min-width: 78px;
  height: 32px;
  padding: 0 14px !important;
  border: 1px solid rgba(var(--solar-border-strong-rgb, 54, 191, 250), 0.5) !important;
  border-radius: 4px;
  background: rgba(var(--solar-primary-rgb, 8, 127, 178), 0.3) !important;
  color: var(--solar-text, #72d8ff) !important;
  font-weight: 600;
}
.feedback-handle-btn:hover { border-color: var(--solar-border-strong, #36bffa) !important; background: rgba(var(--solar-primary-rgb, 8, 127, 178), 0.58) !important; color: var(--solar-text-strong, #fff) !important; }

.user-cell { display: flex; flex-direction: column; line-height: 20px; }
.user-cell strong { color: var(--solar-text-strong, #dff7ff); font-size: 13px; }
.user-cell small { color: var(--solar-muted, #6eaac1); font-size: 11px; }

.status-pill {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: 24px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: 12px;
  font-size: 12px;
  box-sizing: border-box;
}

.status-pill.is-pending { border-color: rgba(255, 183, 77, 0.48); background: rgba(255, 183, 77, 0.12); color: var(--solar-warning, #ffc76a); }
.status-pill.is-processing { border-color: rgba(var(--solar-border-strong-rgb, 54, 191, 250), 0.5); background: rgba(var(--solar-primary-rgb, 54, 191, 250), 0.12); color: var(--solar-text, #67d4ff); }
.status-pill.is-done { border-color: rgba(57, 217, 138, 0.5); background: rgba(57, 217, 138, 0.12); color: var(--solar-text, #72e5aa); }

.dialog-title { display: flex; align-items: center; justify-content: space-between; padding-right: 28px; }
.dialog-title > div { display: flex; align-items: baseline; gap: 9px; }
.dialog-title strong { color: var(--solar-text-strong, #effcff); font-size: 18px; }
.dialog-title > div span { color: var(--solar-muted, #6daec6); font-size: 12px; }
.dialog-content { max-height: 66vh; min-height: 180px; padding-right: 4px; overflow-y: auto; }

.feedback-summary {
  padding: 18px;
  border: 1px solid rgba(75, 195, 255, 0.24);
  border-radius: 8px;
  background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.45);
}

.summary-meta { display: flex; flex-wrap: wrap; gap: 18px; color: var(--solar-text, #7ab9d0); font-size: 12px; }
.summary-meta i { margin-right: 5px; color: var(--solar-text, #49c8f4); }
.feedback-summary h2 { margin: 14px 0 8px; color: var(--solar-text-strong, #f2fcff); font-size: 19px; }
.feedback-summary p { margin: 0; color: var(--solar-text-strong, #b9ddea); line-height: 1.75; white-space: pre-wrap; word-break: break-word; }
.record-section { padding: 20px 3px 4px; }
.section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; color: var(--solar-text-strong, #dff7ff); }
.section-title span { color: var(--solar-muted, #70abc0); font-size: 12px; }
.record-card { padding: 12px 14px; border: 1px solid rgba(75, 195, 255, 0.2); border-radius: 7px; background: rgba(var(--solar-surface-rgb, 0, 40, 70), 0.48); }
.record-card > div { display: flex; align-items: center; gap: 10px; }
.record-card strong { color: var(--solar-text-strong, #dff7ff); }
.record-card span { color: var(--solar-text, #68cee9); font-size: 12px; }
.record-card p { margin: 8px 0 0; color: var(--solar-text-strong, #b3dbe8); line-height: 1.6; white-space: pre-wrap; }
.record-card p.muted, .empty-record { color: var(--solar-muted, #648fa1); }
.empty-record { padding: 18px; text-align: center; border: 1px dashed rgba(75, 195, 255, 0.2); border-radius: 7px; }
.handle-form { margin-top: 18px; padding: 18px; border-top: 1px solid rgba(75, 195, 255, 0.2); background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.28); }
.status-transition { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.status-transition i { color: var(--solar-text, #69cfee); font-size: 16px; }
.completed-hint { margin-top: 18px; padding: 16px 18px; border: 1px solid rgba(57, 217, 138, 0.28); border-radius: 7px; background: rgba(57, 217, 138, 0.08); color: var(--solar-text, #8be9b8); }
.completed-hint i { margin-right: 7px; }
.dialog-footer { display: flex; justify-content: flex-end; }

.feedback-manage-view /deep/ .feedback-detail-dialog .el-input__inner,
.feedback-manage-view /deep/ .feedback-detail-dialog .el-textarea__inner {
  border-color: rgba(75, 195, 255, 0.34) !important;
  background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.72) !important;
  color: var(--solar-text-strong, #dff7ff) !important;
}

.feedback-manage-view /deep/ .feedback-detail-dialog .el-input__inner:focus,
.feedback-manage-view /deep/ .feedback-detail-dialog .el-textarea__inner:focus { border-color: var(--solar-border-strong, #27b7ef) !important; }
.feedback-manage-view /deep/ .management-table .el-button--text { color: var(--solar-text, #55cef5); }
.feedback-manage-view /deep/ .feedback-detail-dialog { border: 1px solid rgba(75, 195, 255, 0.35); border-radius: 10px; background: var(--solar-page, #00213d); box-shadow: 0 24px 70px rgba(0, 4, 14, 0.6); }
.feedback-manage-view /deep/ .feedback-detail-dialog .el-dialog__header { padding: 18px 22px; border-bottom: 1px solid rgba(75, 195, 255, 0.2); }
.feedback-manage-view /deep/ .feedback-detail-dialog .el-dialog__body { padding: 20px 22px 10px; color: var(--solar-text-strong, #b9ddea); }
.feedback-manage-view /deep/ .feedback-detail-dialog .el-dialog__footer { padding: 14px 22px 18px; border-top: 1px solid rgba(75, 195, 255, 0.18); }
.feedback-manage-view /deep/ .feedback-detail-dialog .el-form-item__label { color: var(--solar-text-strong, #a8d7e7); }
.feedback-manage-view /deep/ .feedback-detail-dialog .el-textarea .el-input__count,
.feedback-manage-view /deep/ .feedback-detail-dialog .el-textarea .el-input__count-inner {
  background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.96) !important;
  color: var(--solar-text, #82bfd5) !important;
}

@media (max-width: 760px) {
  .feedback-manage-view .search-item,
  .feedback-manage-view .feedback-keyword-filter,
  .feedback-manage-view .feedback-select-filter {
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
  }

  .feedback-manage-view /deep/ .search-btn.el-button,
  .feedback-manage-view /deep/ .feedback-reset-btn.el-button {
    width: 100%;
    min-width: 0;
  }
}
</style>

<style>
/* 弹窗可能被 Element UI 移动到页面遮罩层，使用专属全局类避免加载时回退为默认白底。 */
.feedback-detail-dialog.el-dialog {
  border: 1px solid rgba(75, 195, 255, 0.35) !important;
  border-radius: 10px !important;
  background: var(--solar-page, #00213d) !important;
  color: var(--solar-text-strong, #b9ddea) !important;
  box-shadow: 0 24px 70px rgba(0, 4, 14, 0.6) !important;
}

.feedback-detail-dialog .el-dialog__header,
.feedback-detail-dialog .el-dialog__body,
.feedback-detail-dialog .el-dialog__footer {
  background: var(--solar-page, #00213d) !important;
}

.feedback-detail-dialog .el-loading-mask {
  background-color: rgba(var(--solar-page-rgb, 0, 18, 36), 0.9) !important;
}

.feedback-detail-dialog .el-textarea .el-input__count,
.feedback-detail-dialog .el-textarea .el-input__count-inner {
  background: rgba(var(--solar-page-rgb, 0, 18, 36), 0.96) !important;
  color: var(--solar-text, #82bfd5) !important;
}
</style>
